import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { firestoreService } from '@/services/firestoreService';

interface MarketMover {
  id: string;
  ticker: string;
  companyName: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  date: any;
  type: 'gainer' | 'loser';
}

export const MarketMovers: React.FC = () => {
  const [movers, setMovers] = useState<MarketMover[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<'gainer' | 'loser'>('gainer');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    loadMovers();
  }, [type]);

  const loadMovers = async () => {
    try {
      setLoading(true);
      const moversData = await firestoreService.getDocuments<MarketMover>('marketMovers', [
        firestoreService.orderBy('date', 'desc'),
        firestoreService.limit(500),
      ]);

      if (moversData.length === 0) {
        setMovers([]);
        setLastUpdated(null);
        return;
      }

      // Keep only rows from the most recent snapshot date, then sort by % change.
      const dateToMs = (d: any): number => {
        const dateObj = d?.toDate?.() ? d.toDate() : new Date(d);
        return Number.isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
      };
      const latestMs = Math.max(...moversData.map((m) => dateToMs(m.date)));
      const sameSnapshot = moversData.filter(
        (m) => dateToMs(m.date) === latestMs && m.type === type
      );
      const sorted = [...sameSnapshot].sort((a, b) =>
        type === 'gainer'
          ? b.changePercent - a.changePercent
          : a.changePercent - b.changePercent
      );

      setMovers(sorted.slice(0, 20));
      setLastUpdated(new Date(latestMs));
    } catch (error) {
      console.error('Failed to load market movers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Market Movers</h1>
          <p className="body-text text-gray-600">
            Biggest gainers and losers based on price and volume
          </p>
          {lastUpdated && (
            <p className="body-text text-sm text-gray-500 mt-1">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex gap-4 mb-6">
          <Button
            variant={type === 'gainer' ? 'primary' : 'outline'}
            onClick={() => setType('gainer')}
          >
            Top Gainers
          </Button>
          <Button
            variant={type === 'loser' ? 'primary' : 'outline'}
            onClick={() => setType('loser')}
          >
            Top Losers
          </Button>
        </div>

        {movers.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600">
                No market movers data available yet. Data is updated throughout the trading day.
              </p>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 sub-header">Ticker</th>
                    <th className="text-left py-3 px-4 sub-header">Company</th>
                    <th className="text-right py-3 px-4 sub-header">Price</th>
                    <th className="text-right py-3 px-4 sub-header">Change</th>
                    <th className="text-right py-3 px-4 sub-header">Change %</th>
                    <th className="text-right py-3 px-4 sub-header">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {movers.map((mover) => (
                    <tr key={mover.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 body-text font-mono font-medium">{mover.ticker}</td>
                      <td className="py-3 px-4 body-text">{mover.companyName}</td>
                      <td className="py-3 px-4 text-right body-text">${mover.price.toFixed(2)}</td>
                      <td className={`py-3 px-4 text-right body-text font-medium ${
                        mover.change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {mover.change >= 0 ? '+' : ''}{mover.change.toFixed(2)}
                      </td>
                      <td className={`py-3 px-4 text-right body-text font-medium ${
                        mover.changePercent >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {mover.changePercent >= 0 ? '+' : ''}{mover.changePercent.toFixed(2)}%
                      </td>
                      <td className="py-3 px-4 text-right body-text text-gray-600">
                        {mover.volume.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

